import orderBy from 'lodash/orderBy';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import type { MyAssignment, ScoreBase } from '../../../../api/assignment';
import {
  isScorableMedialink,
  validateAndGetLinkFromMedialink,
  type ContentType,
  type Medialink,
} from '../../../../api/medialink';
import { SlideSet } from '../../../../api/slide-set';
import {
  isBundle,
  isLti,
  validateAndGetLinkFromUserMaterial,
  type LtiSharedUserMaterial,
  type LtiUserMaterial,
} from '../../../../api/user-material';
import IdentityContext from '../../../../contexts/IdentityContext';
import MyAssignmentsContext from '../../../../contexts/MyAssignmentsContext';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import ModuleContext from '../../_contexts/ModuleContext';
import useSlideSets from '../../hooks/useSlideSets';
import {
  deletePinnedMaterials,
  getPinnedMaterial,
  postPinnedMaterials,
  type PinnedMaterialTypeParam,
} from '../api/pinned-material';
import { sortByContentType } from '../components/utils';
import MedialinksContext from './MedialinksContext';
import SharedMaterialsContext from './SharedMaterialsContext';
import TableOfContentsContext from './TableOfContentsContext';
import UserMaterialContext from './UserMaterialContext';

export type PinnedMaterialType = 'medialink' | 'user-material' | 'slide-set';

export interface PinnedMaterial {
  id: string | number;
  type: PinnedMaterialType;
  name: string;
  score?: ScoreBase;
  contentType?: ContentType;
  isShared?: boolean;
  isAssignment?: boolean;
  deadline?: string;
  group?: 'student' | 'teacher';
  versionId?: number;
  state?: SlideSet['state'];
  isUserSlideSet?: boolean;
  href?: string; // to render anchor tags for non-embedded links
  unachievable?: boolean;
  isBundle?: boolean;
}

interface PinnedMaterialContextValue {
  pinnedMaterialList: PinnedMaterial[];
  getPinnedMaterialList: (type: PinnedMaterialType) => PinnedMaterial[];
  pinMaterials: (materials: { materialId: number | string; materialType: PinnedMaterialType }[]) => Promise<void>;
  unpinMaterials: (materials: { materialId: number | string; materialType: PinnedMaterialType }[]) => Promise<void>;
  showPinned: boolean;
  handleShowPinned: () => void;
}

const PinnedMaterialContext = createContext<PinnedMaterialContextValue>({
  pinnedMaterialList: [],
  getPinnedMaterialList: () => [],
  pinMaterials: () => Promise.reject(),
  unpinMaterials: () => Promise.reject(),
  showPinned: false,
  handleShowPinned: () => {},
});

function toPathType(type: string) {
  return type.toLocaleUpperCase().replace('-', '_') as PinnedMaterialTypeParam;
}

function getScore(material: LtiUserMaterial | MyAssignment | LtiSharedUserMaterial | Medialink): ScoreBase {
  // maximum and amountOfItems should not be set to 0, ideally this should be kept equal with the logic in each material context
  return material.score ? material.score : { maximum: 1, achieved: 0, achievable: 0, amountOfItems: 1 };
}

export default PinnedMaterialContext;

export function PinnedMaterialContextProvider({ children }: PropsWithChildren) {
  const handleError = useThrowAsyncError();

  const [showPinned, setShowPinned] = useState(false);
  const { assignments } = useContext(MyAssignmentsContext);
  const { sharedMaterials, isLoading: isLoadingSM } = useContext(SharedMaterialsContext);
  const { medialinks, isLoading: isLoadingML } = useContext(MedialinksContext);
  const { userMaterial, isLoading: isLoadingUM } = useContext(UserMaterialContext);
  const { slideSets, isLoading: isLoadingS } = useSlideSets();
  const { identity } = useContext(IdentityContext);
  const { module, selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);

  const [pinnedMaterialList, setPinnedMaterialList] = useState<PinnedMaterial[]>([]);

  const fetchPinnedMaterial = useCallback(async () => {
    if (!selectedModule) return;

    try {
      const data = await getPinnedMaterial(selectedModule.id);
      const allMaterialIds = [
        ...data[selectedModule.id].medialinks,
        ...data[selectedModule.id].slideSets,
        ...data[selectedModule.id].userMaterial,
      ];

      const materialList: PinnedMaterial[] = [];

      if (identity.isTeacherAlike) {
        userMaterial
          .filter(m => data[selectedModule.id].userMaterial.includes(m.id))
          .forEach(m => {
            materialList.push({
              id: m.id,
              type: 'user-material',
              name: m.name,
              isShared: m.shared,
              isAssignment: !!m.assignment,
              score: isLti(m) ? getScore(m) : undefined,
              href: validateAndGetLinkFromUserMaterial(m),
              isBundle: isBundle(m),
            });
          });
      } else {
        assignments
          .filter(a => a.moduleId === module?.id && a.nodeId === selectedNode?.id)
          .filter(a => allMaterialIds.includes(a.materialId))
          .forEach(a => {
            materialList.push({
              id: a.materialId,
              type: a.materialType,
              name: a.name,
              contentType: a.contentType,
              isAssignment: true,
              deadline: a.to,
              score: a.score,
              unachievable: a.unachievable,
            });
          });

        sharedMaterials
          .filter(m => allMaterialIds.includes(m.id))
          .forEach(m => {
            if (materialList.some(pinned => pinned.id === m.id)) return;
            materialList.push({
              id: m.id,
              type: m.materialType,
              name: m.name,
              score: m.materialType === 'user-material' && isLti(m) ? getScore(m) : undefined,
              href: m.materialType === 'user-material' ? validateAndGetLinkFromUserMaterial(m) : undefined,
              versionId: m.materialType === 'slide-set' ? m.versionId : undefined,
              state: m.materialType === 'slide-set' ? m.state : undefined,
              isUserSlideSet: m.materialType === 'slide-set' ? Boolean(m.owner) : undefined,
              isBundle: m.materialType === 'user-material' ? isBundle(m) : undefined,
            });
          });
      }

      // Pelckmans slide sets reach students through this list, not through sharedMaterials
      slideSets
        .filter(s => data[selectedModule.id].slideSets.includes(s.id))
        .forEach(m => {
          if (materialList.some(pinned => pinned.id === m.id)) return;
          materialList.push({
            id: m.id,
            type: 'slide-set',
            name: m.name,
            isShared: m.shared,
            versionId: m.versionId,
            state: m.state,
            isUserSlideSet: Boolean(m.userId),
          });
        });

      let sortedMedialinks = sortByContentType(medialinks);
      if (!identity.isTeacherAlike) {
        // Put group=student before group=teacher when user is student
        sortedMedialinks = orderBy(sortedMedialinks, ['group'], ['desc']);
      }

      sortedMedialinks
        .filter(m => data[selectedModule.id].medialinks.includes(m.id))
        .forEach(m => {
          if (materialList.some(pinned => pinned.id === m.id)) return;
          materialList.push({
            id: m.id,
            type: 'medialink',
            name: m.name,
            contentType: m.contentType,
            isShared: m.shares.length > 0,
            isAssignment: m.assignments.length > 0,
            group: m.group,
            score: isScorableMedialink(m) ? getScore(m) : undefined,
            href: validateAndGetLinkFromMedialink(m),
            unachievable: m.assessmentQExercise?.isAdaptive && m.score === undefined ? false : m.unachievable,
          });
        });

      setPinnedMaterialList(materialList);
    } catch (e) {
      handleError(e);
    }
  }, [
    assignments,
    handleError,
    identity.isTeacherAlike,
    medialinks,
    module?.id,
    selectedModule,
    selectedNode?.id,
    sharedMaterials,
    slideSets,
    userMaterial,
  ]);

  // Fetch pinnedMaterial when done loading
  useEffect(() => {
    if (!isLoadingSM && !isLoadingML && !isLoadingS && !isLoadingUM) fetchPinnedMaterial();
  }, [fetchPinnedMaterial, isLoadingML, isLoadingS, isLoadingSM, isLoadingUM]);

  const getPinnedMaterialList = (type: PinnedMaterialType) => {
    return pinnedMaterialList.filter(m => m.type === type);
  };

  const pinMaterials = async (
    materials: { materialId: number | string; materialType: PinnedMaterialType }[],
  ): Promise<void> => {
    if (!selectedModule) return;
    try {
      await postPinnedMaterials(
        selectedModule.id,
        materials.map(m => ({ materialId: m.materialId, materialType: toPathType(m.materialType) })),
      );
      fetchPinnedMaterial();
    } catch (e) {
      handleError(e);
    }
  };

  const unpinMaterials = async (
    materials: { materialId: number | string; materialType: PinnedMaterialType }[],
  ): Promise<void> => {
    if (!selectedModule) return;

    try {
      await deletePinnedMaterials(
        selectedModule.id,
        materials.map(m => ({ materialId: m.materialId, materialType: toPathType(m.materialType) })),
      );
      fetchPinnedMaterial();
    } catch (e) {
      handleError(e);
    }
  };

  const handleShowPinned = () => {
    setShowPinned(previous => !previous);
  };

  return (
    <PinnedMaterialContext.Provider
      value={{
        pinnedMaterialList,
        getPinnedMaterialList,
        pinMaterials,
        unpinMaterials,
        showPinned,
        handleShowPinned,
      }}
    >
      {children}
    </PinnedMaterialContext.Provider>
  );
}
